import React, { useState, useRef, useEffect } from "react";
import * as XLSX from "xlsx";

/**
 * FairwayRadar — Contacts Builder v4.0
 * * FEATURES:
 * - Specific "Fairway Digital Media" Logo & Branding.
 * - Legal/Privacy Disclaimer.
 * - Capabilities Grid (The "Classy" top section).
 * - Multi-Mode: LinkedIn Reactions (HTML Injection) + Instagram Lists.
 * - Improved Parsing: Better Company/Title separation.
 */

/* ===================== ASSETS & TOKENS ===================== */

const LOGO_URL =
  "https://pub-065b7688176a4969bec21707aed69fdc.r2.dev/logos/Final%20Logo_400.png";

const CAPABILITIES = [
  "Employer/Email/Phone verification (Data Hygiene-ready export)",
  "Contact discovery & enrichment (Full-Contact-Data ready export)",
  "Name ↔ domain normalization and dedupe",
  "Role/title parsing with company detection",
  "Connection degree capture (1st/2nd/3rd+)",
  "CSV/XLSX exports with clean headers",
  "PII-safe workflows (no emails required to parse)",
  "Social Profile Link Reconstruction & Capture",
];

/* ===================== ICONS (Inline SVG) ===================== */
const Icons = {
  Shield: () => (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  ),
  Check: () => (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  ),
  Filter: () => (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
  ),
  Download: () => (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" x2="12" y1="15" y2="3" />
    </svg>
  ),
  ExternalLink: () => (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" x2="21" y1="14" y2="3" />
    </svg>
  ),
  Trash: () => (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
  ),
};

/* ===================== UTILS & EXPORTS ===================== */

const normalizeText = (s = "") =>
  s
    .normalize("NFKC")
    .replace(/\u00a0/g, " ")
    .replace(/[ \t]+/g, " ")
    .trim();

function downloadCSV(filename, rows) {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const esc = (v) =>
    `"${String(v ?? "")
      .replace(/"/g, '""')
      .replace(/\r?\n/g, " ")}"`;
  const csv = [
    headers.join(","),
    ...rows.map((r) => headers.map((h) => esc(r[h])).join(",")),
  ].join("\n");
  const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

function downloadXLSX(filename, rows, sheetName = "Contacts") {
  if (!rows.length) return;
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  XLSX.writeFile(wb, filename);
}

/* ===================== PARSING LOGIC ===================== */

// --- LINKEDIN HELPERS ---
const COMPANY_HINTS = [
  " inc",
  " llc",
  " ltd",
  " group",
  " corp",
  " technology",
  " systems",
  " solutions",
  " media",
  " marketing",
  " ventures",
  " capital",
  " software",
];

function cleanLinkedInName(s) {
  let t = s;
  t = t.replace(/^View\s+(.+?)’s profile.*$/i, "$1");
  t = t.replace(/^View\s+(.+?)'s profile.*$/i, "$1");
  t = t.replace(/[\|•·]+.*$/, "");
  t = t.replace(/\s{2,}/g, " ").trim();
  return t;
}

function splitTitleCompany(headline) {
  const h = normalizeText(headline);
  // 1. Try "Title at Company"
  const m = h.match(/^(?<title>.+?)\s+(?:at|@)\s+(?<company>.+)$/i);
  if (m?.groups)
    return [m.groups.title.replace(/[ |,-]+$/g, ""), m.groups.company.trim()];

  // 2. Try splitting by pipe/dash
  const parts = h
    .split(/[|]|\u2013| - /)
    .map((p) => p.trim())
    .filter(Boolean);

  if (parts.length >= 2) {
    // Assume last part is company
    return [parts.slice(0, -1).join(" | "), parts[parts.length - 1]];
  }

  // 3. Fallback: Entire thing is title, no company detected
  return [h, ""];
}

function parseLinkedInBlocks(text, markers) {
  const lines = text.split(/\r?\n/).map((l) => normalizeText(l));
  const EMOTION = new Set(
    (markers || []).map((m) => (m || "").trim().toLowerCase()).filter(Boolean)
  );
  const entries = [];
  let i = 0;

  const isProfileURL = (s) =>
    /linkedin\.com\/in\//i.test(s) || /lnkd\.in\//i.test(s);

  const findUrl = (arr) => {
    const found = arr.find((l) => isProfileURL(l));
    if (!found) return { url: "", handle: "" };
    const handle = found.match(/in\/([A-Za-z0-9\-_%\.]+)/)?.[1] || "";
    return { url: found, handle };
  };

  while (i < lines.length) {
    if (!EMOTION.has((lines[i] || "").toLowerCase())) {
      i++;
      continue;
    }

    i++; // skip marker
    const block = [];
    while (i < lines.length && !EMOTION.has((lines[i] || "").toLowerCase())) {
      block.push(lines[i]);
      i++;
    }

    let b = 0;
    while (b < block.length && !block[b]) b++;
    if (b >= block.length) continue;

    const name = cleanLinkedInName(block[b]);
    b++;

    let connection = "";
    let headline = "";

    // Look for headline vs connection vs url
    while (b < block.length) {
      const line = block[b];

      // Skip/Capture Connection
      if (
        /^(?:[-•·]\s*)?(?:1st|2nd|3rd|degree)/i.test(line) ||
        /^view .+ profile/i.test(line)
      ) {
        const match = line.match(/\b(1st|2nd|3rd\+?)\b/i);
        if (match) connection = match[1];
        b++;
        continue;
      }
      // Skip URL (captured later)
      if (isProfileURL(line)) {
        b++;
        continue;
      }

      // If not empty, it's the headline
      if (line.trim()) {
        headline = line;
        break;
      }
      b++;
    }

    const [title_role, company] = splitTitleCompany(headline);
    const { url: profile_url, handle: profile_handle } = findUrl(block);

    entries.push({
      name: name.replace(/^(like|love|celebrate|insightful|support)\s+/i, ""),
      title_role,
      company,
      connection,
      profile_url,
      profile_handle,
    });
  }

  const seen = new Set();
  return entries.filter((e) => {
    const k = e.name + e.title_role;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

// --- INSTAGRAM HELPERS ---
function parseInstagramBlocks(text, baseUrl) {
  const lines = text.split(/\r?\n/).map((l) => normalizeText(l));
  const entries = [];
  let i = 0;
  while (i < lines.length) {
    while (i < lines.length) {
      const line = lines[i].toLowerCase();
      if (!line || line.includes("follow") || line.includes("profile picture"))
        i++;
      else break;
    }
    if (i >= lines.length) break;
    const username = lines[i];
    i++;
    while (i < lines.length) {
      const line = lines[i].toLowerCase();
      if (!line || line.includes("follow") || line.includes("profile picture"))
        i++;
      else break;
    }
    let display_name = "";
    if (i < lines.length) {
      display_name = lines[i].replace(/[\|•·\s]+.*$/, "").trim();
      i++;
    }
    if (username) {
      entries.push({
        username,
        display_name,
        profile_link: baseUrl + username,
      });
    }
  }
  const seen = new Set();
  return entries.filter((e) => {
    const k = e.username.toLowerCase();
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

function handleLinkedInPaste(e, setText, taRef) {
  try {
    const html = e.clipboardData?.getData("text/html") || "";
    const plain = e.clipboardData?.getData("text/plain") || "";
    if (!html) return;
    const doc = new DOMParser().parseFromString(html, "text/html");
    const urls = Array.from(doc.querySelectorAll("a[href]"))
      .map((a) => a.getAttribute("href"))
      .filter((h) => /linkedin\.com\/in\//i.test(h));
    if (!urls.length) return;

    e.preventDefault();
    const lines = plain.split(/\r?\n/);
    let idx = 0;
    for (let i = 0; i < lines.length && idx < urls.length; i++) {
      if (/^view .+ profile/i.test(lines[i])) {
        if (!/http/.test(lines[i + 1] || "")) {
          lines.splice(i + 1, 0, urls[idx]);
          idx++;
          i++;
        }
      }
    }
    while (idx < urls.length) lines.push(urls[idx++]);
    const enhanced = lines.join("\n");
    const el = taRef.current;
    if (!el) {
      setText((prev) => prev + enhanced);
      return;
    }
    const start = el.selectionStart;
    const next =
      el.value.slice(0, start) + enhanced + el.value.slice(el.selectionEnd);
    setText(next);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + enhanced.length, start + enhanced.length);
    });
  } catch (err) {
    /* fallback */
  }
}

/* ===================== UI COMPONENT ===================== */

export default function FairwayRadarTool() {
  const [mode, setMode] = useState("linkedin");
  const [text, setText] = useState("");
  const [rows, setRows] = useState([]);
  const [markers, setMarkers] = useState(
    "like, love, celebrate, insightful, support"
  );
  const [igBaseUrl, setIgBaseUrl] = useState("https://www.instagram.com/");
  const taRef = useRef(null);

  const clear = () => {
    setText("");
    setRows([]);
    taRef.current?.focus();
  };

  const convert = () => {
    if (mode === "linkedin") {
      const mList = markers
        .split(",")
        .map((m) => m.trim())
        .filter(Boolean);
      setRows(parseLinkedInBlocks(text, mList));
    } else {
      setRows(parseInstagramBlocks(text, igBaseUrl));
    }
  };

  const loadSample = () => {
    if (mode === "linkedin")
      setText(
        `like\nJohn Doe\nView John Doe's profile • 2nd\nVP Sales at Acme Inc\n\nlove\nJane Smith\nView Jane Smith's profile\nDirector @ Beta Corp`
      );
    else
      setText(
        `user123's profile picture\nuser123\nJohn Doe\nFollow\n\ncool_brand's profile picture\ncool_brand\nCool Brand Official\nFollow`
      );
  };

  const exportData = (type) => {
    const date = new Date().toISOString().slice(0, 10);
    const filename = `fairway_${mode}_${date}.${type}`;
    let exportRows = [];
    if (mode === "linkedin") {
      exportRows = rows.map((r) => ({
        full_name: r.name,
        title: r.title_role,
        company: r.company,
        connection: r.connection,
        linkedin_url: r.profile_url,
      }));
    } else {
      exportRows = rows.map((r) => ({
        username: r.username,
        display_name: r.display_name,
        profile_link: r.profile_link,
      }));
    }
    if (type === "csv") downloadCSV(filename, exportRows);
    else downloadXLSX(filename, exportRows);
  };

  return (
    <div className="fr-app">
      {/* --- STYLES --- */}
      <style>{`
        /* --- RESET --- */
        .fr-app {
          font-family: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
          background-color: #020617; /* Slate 950 */
          color: #e2e8f0; /* Slate 200 */
          min-height: 100vh;
          padding-top: 100px; /* CLEAR HEADER */
          padding-bottom: 60px;
        }
        * { box-sizing: border-box; }

        /* --- LAYOUT --- */
        .fr-container { max-width: 1400px; margin: 0 auto; padding: 0 24px; }
        
        /* --- HEADER --- */
        .fr-header {
          position: fixed; top: 0; left: 0; right: 0; z-index: 50;
          background: rgba(2, 6, 23, 0.95);
          backdrop-filter: blur(12px);
          border-bottom: 1px solid #1e293b;
          height: 80px;
          display: flex; align-items: center; justify-content: space-between;
          padding: 0 32px;
          box-shadow: 0 4px 20px -1px rgba(0, 0, 0, 0.5);
        }
        .fr-brand { display: flex; align-items: center; gap: 16px; }
        .fr-logo { height: 48px; width: auto; }
        .fr-brand-name { font-size: 22px; font-weight: 700; color: white; letter-spacing: -0.025em; line-height: 1.2; }
        .fr-brand-sub { color: #64748b; font-weight: 400; font-size: 14px; }

        /* --- ALERT / LEGAL --- */
        .fr-alert {
          background: rgba(234, 179, 8, 0.08); 
          border: 1px solid rgba(234, 179, 8, 0.2);
          color: #fef08a; padding: 12px 16px; border-radius: 8px; font-size: 13px;
          display: flex; gap: 12px; align-items: flex-start; margin-bottom: 24px;
          line-height: 1.5;
        }
        .fr-alert-icon { color: #eab308; margin-top: 2px; flex-shrink: 0; }

        /* --- CAPABILITIES GRID --- */
        .fr-cap-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 12px; margin-bottom: 32px; }
        .fr-cap-item {
          background: rgba(15, 23, 42, 0.6); border: 1px solid #1e293b; border-radius: 8px;
          padding: 12px 16px; color: #cbd5e1; font-size: 13px; font-weight: 500;
          display: flex; align-items: flex-start; gap: 10px;
        }
        .fr-cap-icon { color: #34d399; margin-top: 1px; flex-shrink: 0; }

        /* --- CARDS --- */
        .fr-card {
          background: rgba(15, 23, 42, 0.5);
          backdrop-filter: blur(8px);
          border: 1px solid #1e293b;
          border-radius: 12px;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
          overflow: hidden;
          margin-bottom: 24px;
        }

        /* --- TABS --- */
        .fr-tabs { display: flex; border-bottom: 1px solid #1e293b; background: rgba(2, 6, 23, 0.5); }
        .fr-tab {
          padding: 16px 24px; cursor: pointer; font-size: 14px; font-weight: 600; color: #94a3b8;
          border: none; border-right: 1px solid #1e293b; background: transparent; transition: all 0.2s;
        }
        .fr-tab:hover { color: #e2e8f0; background: #1e293b; }
        .fr-tab.active { color: #34d399; background: #0f172a; box-shadow: inset 0 -2px 0 #34d399; }

        /* --- INPUTS & CONTROLS --- */
        .fr-controls { padding: 16px; display: flex; flex-wrap: wrap; gap: 16px; align-items: end; background: #0f172a; border-bottom: 1px solid #1e293b; }
        .fr-input-group { flex-grow: 1; min-width: 250px; }
        .fr-label { display: block; font-size: 11px; font-weight: 700; text-transform: uppercase; color: #64748b; margin-bottom: 6px; letter-spacing: 0.05em; }
        .fr-input {
          width: 100%; background: #020617; border: 1px solid #1e293b; color: #e2e8f0;
          border-radius: 6px; padding: 10px 12px; font-size: 14px; outline: none; transition: all 0.2s;
        }
        .fr-input:focus { border-color: #34d399; box-shadow: 0 0 0 2px rgba(52, 211, 153, 0.2); }

        /* --- TEXTAREA --- */
        .fr-textarea {
          width: 100%; height: 300px; background: #020617; border: none; color: #e2e8f0;
          padding: 24px; font-family: "JetBrains Mono", ui-monospace, monospace;
          font-size: 13px; line-height: 1.6; resize: vertical; outline: none; display: block;
        }
        .fr-textarea:focus { box-shadow: inset 0 0 0 2px rgba(52, 211, 153, 0.2); }

        /* --- BUTTONS --- */
        .fr-btn {
          display: inline-flex; items-center; gap: 8px; padding: 9px 18px; border-radius: 6px;
          font-size: 13px; font-weight: 600; cursor: pointer; border: 1px solid #334155;
          background: #1e293b; color: #e2e8f0; transition: all 0.2s;
        }
        .fr-btn:hover { background: #334155; transform: translateY(-1px); }
        .fr-btn-primary { background: #059669; border-color: #047857; color: white; box-shadow: 0 4px 12px rgba(5, 150, 105, 0.3); }
        .fr-btn-primary:hover { background: #047857; }

        /* --- TABLE --- */
        .fr-table-wrap { overflow-x: auto; }
        .fr-table { width: 100%; border-collapse: separate; border-spacing: 0; }
        .fr-th {
          text-align: left; padding: 14px 20px; font-size: 11px; font-weight: 700; text-transform: uppercase;
          letter-spacing: 0.05em; color: #64748b; border-bottom: 1px solid #1e293b; background: #0f172a; white-space: nowrap;
        }
        .fr-td { padding: 14px 20px; border-bottom: 1px solid #1e293b; vertical-align: middle; font-size: 14px; }
        .fr-tr:last-child .fr-td { border-bottom: none; }
        .fr-tr:hover { background: rgba(30, 41, 59, 0.5); }
        
        /* --- BADGES --- */
        .fr-badge { display: inline-flex; align-items: center; padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: 600; }
        .fr-badge-title { background: rgba(56, 189, 248, 0.1); color: #38bdf8; border: 1px solid rgba(56, 189, 248, 0.2); }
        .fr-badge-co { color: #94a3b8; font-weight: 500; }
        .fr-link-btn { color: #34d399; text-decoration: none; font-weight: 600; font-size: 13px; display: flex; align-items: center; gap: 4px; }
        .fr-link-btn:hover { text-decoration: underline; }
      `}</style>

      {/* --- HEADER --- */}
      <header className="fr-header">
        <div className="fr-brand">
          <img src={LOGO_URL} alt="Fairway Digital Media" className="fr-logo" />
          <div>
            <div className="fr-brand-name">Fairway Digital Media</div>
            <div className="fr-brand-sub">Contacts Builder</div>
          </div>
        </div>
        <div className="fr-brand">
          <span
            className="fr-badge"
            style={{ background: "rgba(52, 211, 153, 0.1)", color: "#34d399" }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                background: "#34d399",
                borderRadius: "50%",
                marginRight: 6,
              }}
            ></span>
            Ready for input
          </span>
        </div>
      </header>

      <div className="fr-container">
        {/* --- LEGAL DISCLAIMER --- */}
        <div className="fr-alert">
          <div className="fr-alert-icon">
            <Icons.Shield />
          </div>
          <div>
            <strong>Legal Disclaimer & Privacy Notice:</strong> This tool runs
            entirely in your browser (Client-Side). No data is sent to external
            servers. You are responsible for ensuring that your use of this tool
            complies with the Terms of Service of any platform you interact with
            and all applicable privacy laws (e.g., GDPR, CCPA). Fairway Digital
            Media is not responsible for misuse.
          </div>
        </div>

        {/* --- CAPABILITIES GRID (Classy Top Part) --- */}
        <div className="fr-cap-grid">
          {CAPABILITIES.map((cap, i) => (
            <div key={i} className="fr-cap-item">
              <div className="fr-cap-icon">
                <Icons.Check />
              </div>
              <div>{cap}</div>
            </div>
          ))}
        </div>

        {/* --- MAIN CARD --- */}
        <div className="fr-card">
          <div className="fr-tabs">
            <button
              className={`fr-tab ${mode === "linkedin" ? "active" : ""}`}
              onClick={() => {
                setMode("linkedin");
                clear();
              }}
            >
              LinkedIn Reactions
            </button>
            <button
              className={`fr-tab ${mode === "instagram" ? "active" : ""}`}
              onClick={() => {
                setMode("instagram");
                clear();
              }}
            >
              Instagram Lists
            </button>
          </div>

          <div className="fr-controls">
            {mode === "linkedin" && (
              <div className="fr-input-group">
                <label className="fr-label">
                  <Icons.Filter /> Reaction Filters
                </label>
                <input
                  className="fr-input"
                  value={markers}
                  onChange={(e) => setMarkers(e.target.value)}
                />
              </div>
            )}
            {mode === "instagram" && (
              <div className="fr-input-group">
                <label className="fr-label">Platform URL Prefix</label>
                <input
                  className="fr-input"
                  value={igBaseUrl}
                  onChange={(e) => setIgBaseUrl(e.target.value)}
                />
              </div>
            )}
            <div style={{ display: "flex", gap: 8 }}>
              <button className="fr-btn" onClick={loadSample}>
                Load Sample
              </button>
              <button className="fr-btn" onClick={clear}>
                <Icons.Trash /> Clear
              </button>
              <button className="fr-btn fr-btn-primary" onClick={convert}>
                Parse Data
              </button>
            </div>
          </div>

          <textarea
            ref={taRef}
            className="fr-textarea"
            placeholder={
              mode === "linkedin"
                ? "Paste LinkedIn reaction blocks here..."
                : "Paste Instagram follower list..."
            }
            value={text}
            onChange={(e) => setText(e.target.value)}
            onPaste={
              mode === "linkedin"
                ? (e) => handleLinkedInPaste(e, setText, taRef)
                : undefined
            }
          />
        </div>

        {/* --- RESULTS TABLE --- */}
        {rows.length > 0 && (
          <div className="fr-card">
            <div
              style={{
                padding: "16px 24px",
                borderBottom: "1px solid #1e293b",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div style={{ fontWeight: 700, color: "white" }}>
                Parsed Results ({rows.length})
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button className="fr-btn" onClick={() => exportData("csv")}>
                  <Icons.Download /> CSV
                </button>
                <button className="fr-btn" onClick={() => exportData("xlsx")}>
                  <Icons.Download /> XLSX
                </button>
              </div>
            </div>

            <div className="fr-table-wrap">
              <table className="fr-table">
                <thead>
                  <tr>
                    {mode === "linkedin" ? (
                      <>
                        <th className="fr-th">Name</th>
                        <th className="fr-th">Title / Role</th>
                        <th className="fr-th">Company</th>
                        <th className="fr-th">Degree</th>
                        <th className="fr-th" style={{ textAlign: "right" }}>
                          Profile
                        </th>
                      </>
                    ) : (
                      <>
                        <th className="fr-th">Username</th>
                        <th className="fr-th">Display Name</th>
                        <th className="fr-th" style={{ textAlign: "right" }}>
                          Link
                        </th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} className="fr-tr">
                      {mode === "linkedin" ? (
                        <>
                          <td
                            className="fr-td"
                            style={{ color: "white", fontWeight: 600 }}
                          >
                            {r.name}
                          </td>
                          <td className="fr-td">
                            {r.title_role ? (
                              <span className="fr-badge fr-badge-title">
                                {r.title_role}
                              </span>
                            ) : (
                              <span style={{ opacity: 0.3 }}>—</span>
                            )}
                          </td>
                          <td className="fr-td">
                            <span className="fr-badge-co">
                              {r.company || "—"}
                            </span>
                          </td>
                          <td
                            className="fr-td"
                            style={{ color: "#64748b", fontSize: 12 }}
                          >
                            {r.connection}
                          </td>
                          <td className="fr-td" style={{ textAlign: "right" }}>
                            {r.profile_url ? (
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "flex-end",
                                }}
                              >
                                <a
                                  href={r.profile_url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="fr-link-btn"
                                >
                                  Open <Icons.ExternalLink />
                                </a>
                              </div>
                            ) : (
                              <span style={{ opacity: 0.3, fontSize: 12 }}>
                                No Link
                              </span>
                            )}
                          </td>
                        </>
                      ) : (
                        <>
                          <td
                            className="fr-td"
                            style={{ color: "white", fontWeight: 600 }}
                          >
                            {r.username}
                          </td>
                          <td className="fr-td" style={{ color: "#94a3b8" }}>
                            {r.display_name}
                          </td>
                          <td className="fr-td" style={{ textAlign: "right" }}>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "flex-end",
                              }}
                            >
                              <a
                                href={r.profile_link}
                                target="_blank"
                                rel="noreferrer"
                                className="fr-link-btn"
                              >
                                Open <Icons.ExternalLink />
                              </a>
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
